import fire from "_utils/firebase";
import {
  sendEmailVerification,
  getAuth,
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';

// MFA sign-in state - kept in module scope because the firebase resolver
// object is not serializable and must never be stored in redux
let mfaResolver = null;
let mfaSmsVerificationId = null;
let mfaRecaptchaVerifier = null;

const clearMfaState = () => {
  mfaResolver = null;
  mfaSmsVerificationId = null;
  try {
    mfaRecaptchaVerifier?.clear();
  } catch (e) { /* ignore */ }
  mfaRecaptchaVerifier = null;
};

// generate access token
// forceRefresh: fetch a new token instead of the cached one. needed straight after a
// password change or MFA enrolment, which revoke tokens issued before them
// The logged-in user's token, or null for an anonymous visitor (public pages). Callers send
// the Firebase-UID header only when there is a token; the gateway lets the public routes
// through with none.
export const optionalToken = async () => {
  try {
    return await getToken();
  } catch (e) {
    return null;
  }
};

// header for a public call: the visitor's token when logged in, otherwise none
export const optionalAuthHeader = async () => {
  const token = await optionalToken();
  return token ? { "Firebase-UID": token } : {};
};

export const getToken = async (forceRefresh = false) => {
  const auth = getAuth();
  const currentUser = auth.currentUser || fire.auth().currentUser;

  if (!currentUser) {
    throw new Error("Not authenticated");
  }

  try {
    return await currentUser.getIdToken(forceRefresh);
  } catch (error) {
    throw new Error("error getting token");
  }
};

// sign user up 
const getSignupErrorMessage = (error) => {
  if (error?.code === "auth/invalid-email") {
    return "Invalid email.";
  }

  if (error?.code === "auth/user-disabled") {
    return "This user has been disabled.";
  }

  if (error?.code === "auth/user-not-found") {
    return "This user has not been found.";
  }

  if (error?.code === "auth/wrong-password") {
    return "This password is incorrect.";
  }

  if (
    error?.code === "auth/email-already-in-use" ||
    error?.code === "auth/email-already-exists"
  ) {
    return "This email is already associated with another account.";
  }

  if (error?.message === "iata is not defined" || error === "iata is not defined") {
    return "This IATA is not recognized.";
  }

  return "Error signing up. Try again later.";
};

const getResponseErrorMessage = async (response, fallbackMessage) => {
  try {
    const json = await response.json();
    return json?.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

export const signup = async (form) => {
};

// edit account
export const editAccount = async (details) => {
  return new Promise((resolve, reject) => {
    // change email
    changeEmail(details.email);
  }).catch((error) => {
    reject(
      new Error(
        "Error. Please log out and log back in to change your account information"
      )
    );
  });
};

// update firebase name object
export const updateProfile = async (details) => {
  return new Promise((resolve, reject) => {
    fire
      .auth()
      .currentUser.updateProfile({ displayName: details.name })
      .then(() => resolve())
      .catch(() => reject(new Error("Error updating your account details")));
  });
};

// change email
export const changeEmail = async (email) => {
  return new Promise((resolve, reject) => {
    fire
      .auth()
      .currentUser.updateEmail(email)
      .then(() => {
        // updated sucessfully
        resolve();
      })
      .catch(async (error) => {
        if (error.code === "auth/requires-recent-login") {
          reject(
            new Error(
              "Error. Please log out and log back in to change your account information"
            )
          );
        }
        if (error.code === "auth/email-already-in-use") {
          reject(new Error("This email is already in use"));
        } else {
          reject(new Error("Error changing email. Please try again later."));
        }
      });
  });
};

// change password
export const changePassword = async (password) => {
  return new Promise((resolve, reject) => {
    fire
      .auth()
      .currentUser.updatePassword(password)
      .then(() => {
        resolve();
      })
      .catch(async (error) => {
        if (error.code === "auth/requires-recent-login") {
          reject(
            new Error(
              "Error. Please log out and log back in to change your account information"
            )
          );
        } else {
          reject(new Error("Error changing password. Please try again later."));
        }
      });
  });
};

// update name
export const changeName = async (name) => {
  return new Promise((resolve, reject) => {
    fire
      .auth()
      .currentUser.updateProfile({ displayName: name })
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(new Error("Error updating your account details"));
      });
  });
};

// update user database info
export const updateUserDb = async (user) => {
  const url = "/api/v1/mobileusers/portalusers/" + user.firebaseId;

  return new Promise(async (resolve, reject) => {
    try {
      // make request
      const token = await getToken();
      const response = await fetch(url, {
        method: "PUT",
        body: JSON.stringify(user),
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "firebase-uid": token,
        },
      });
      // handle response
      let json = await response.json();
      if (response.status >= 400 && response.status < 600) {
        throw new Error("Bad details");
      } else {
        resolve(json);
      }
    } catch (e) {
      reject(new Error("error getting user info"));
    }
  });
};

// log in user
export const login = async (email, password) => {
  const auth = getAuth();
  clearMfaState();

  return new Promise(async (resolve, reject) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      resolve({ success: true });
    } catch (error) {

      // second factor required - keep the resolver in module scope (not serializable)
      if (error.code === "auth/multi-factor-auth-required") {
        mfaResolver = getMultiFactorResolver(auth, error);
        const hint = mfaResolver.hints?.[0];

        resolve({
          requiresMfa: true,
          factorId: hint?.factorId || null,
          phoneHint: hint?.phoneNumber || null,
        });
        return;
      }

      if (error.code === "auth/invalid-email") {
        reject(new Error("Invalid email."));
      } else if (error.code === "auth/user-disabled") {
        reject(new Error("This user has been disabled."));
      } else if (error.code === "auth/user-not-found") {
        reject(new Error("This user has not been found."));
      } else if (error.code === "auth/wrong-password") {
        reject(new Error("This password is incorrect."));
      } else {
        reject(new Error("Error logging in. Try again later."));
      }
    }
  });
};

// send the SMS challenge for a phone second factor (requires a pending MFA login)
export const sendMfaSmsChallenge = async (recaptchaContainerId) => {
  const auth = getAuth();

  if (!mfaResolver) {
    throw new Error("No MFA sign in in progress. Please log in again.");
  }

  // firebase@9.23 signature: RecaptchaVerifier(containerOrId, parameters, auth)
  if (!mfaRecaptchaVerifier) {
    mfaRecaptchaVerifier = new RecaptchaVerifier(recaptchaContainerId, { size: 'invisible' }, auth);
  }

  try {
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    mfaSmsVerificationId = await phoneAuthProvider.verifyPhoneNumber(
      {
        multiFactorHint: mfaResolver.hints[0],
        session: mfaResolver.session,
      },
      mfaRecaptchaVerifier
    );
  } catch (error) {
    // reset so a retry can re-render recaptcha cleanly
    try {
      mfaRecaptchaVerifier?.clear();
    } catch (e) { /* ignore */ }
    mfaRecaptchaVerifier = null;
    mfaSmsVerificationId = null;
    const container = document.getElementById(recaptchaContainerId);
    if (container) container.innerHTML = '';
    throw error;
  }

  return true;
};

// complete an MFA sign in with the 6-digit code (TOTP or SMS)
export const verifyMfa = async (mfaCode) => {
  try {
    if (!mfaResolver) {
      throw new Error("MFA session expired. Please log in again.");
    }

    const hint = mfaResolver.hints?.[0];
    if (!hint) {
      throw new Error("MFA session expired. Please log in again.");
    }

    let multiFactorAssertion = null;

    if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
      if (!mfaSmsVerificationId) {
        throw new Error("No SMS code was sent. Please try again.");
      }
      const phoneCredential = PhoneAuthProvider.credential(mfaSmsVerificationId, mfaCode);
      multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneCredential);
    } else {
      multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCode);
    }

    const userCredential = await mfaResolver.resolveSignIn(multiFactorAssertion);
    clearMfaState();

    // ensure tokens are ready before the rest of the app fetches portal data
    if (userCredential?.user) {
      await userCredential.user.getIdToken(true);
    }

    return {
      success: true,
      uid: userCredential?.user?.uid || null,
    };
  } catch (error) {
    console.error('Error verifying MFA code:', error);

    if (error.message?.includes("MFA session") || error.message?.includes("SMS code")) {
      throw error;
    }
    if (error.code === "auth/invalid-verification-code") {
      throw new Error("Invalid verification code. Please try again.");
    }
    if (error.code === "auth/code-expired") {
      throw new Error("Verification code expired. Please log in again.");
    }
    if (error.code === "auth/totp-challenge-timeout" || error.code === "auth/multi-factor-session-expired") {
      clearMfaState();
      throw new Error("MFA session expired. Please log in again.");
    }
    throw new Error("Invalid verification code. Please try again.");
  }
};

// cancel a pending MFA sign in
export const cancelMfa = () => {
  clearMfaState();
};

// logout
export const logout = async () => {
  return new Promise((resolve, reject) => {
    localStorage.clear();
    fire
      .auth()
      .signOut()
      .then(() => {
        resolve();
      })
      .catch(() => reject(new Error("Error logging out")));
  });
};

// get user info
// TODO what is this for
export const getUserByIdConfirmBooking = async (options) => {

  const url = "/api/v1/mobileusers/user/" + options.user_id + "/firebase?firebase_uid=confirming-booking";

  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        throw "Error";
      }
      resolve(json);
    } catch (e) {
      reject(new Error(e));
    }
  });
};

export const getFirebaseInfo = async (userId) => {
  const url = "/api/v1/mobileusers/user/" + userId + "/firebase";

  return new Promise(async (resolve, reject) => {

    const token = await getToken();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "firebase-uid": token
        },
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        //console.log('response from getting user...', json);
        //throw "Error";
      }
      //console.log('response from getting user...', json)
      resolve(json);
    } catch (e) {
      console.log('error getting user info...', e)
      reject(new Error(e));
    }
  });
};

export const getUserByUserId = async (userId) => {

  const url = "/api/v1/mobileusers/user/" + userId + "/firebase";

  return new Promise(async (resolve, reject) => {

    const token = await getToken();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "firebase-uid": token
        },
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        throw "Error";
      }
      resolve(json);
    } catch (e) {
      reject(new Error(e));
    }
  });
};

export const getUserName = async (userId) => {

  const url = "/api/v1/mobileusers/user/" + userId + "/firebase";

  return new Promise(async (resolve, reject) => {

    const token = await getToken();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "firebase-uid": token
        },
      });

      let json = await response.json();
      // console.log('retrieved user...', json)

      if (response.status >= 400 && response.status < 600) {
        throw "Error";
      }

      resolve(json);
    } catch (e) {
      console.log('error getting user...', e)
      reject(new Error(e));
    }
  });
};

export const getUserById = async (options) => {

  // console.log('getting user by ud', options)
  const url = "/api/v1/mobileusers/user/" + (options.user_id || options) + "/firebase" + (options.token ? `?firebase_uid=${options.token}` : '');

  return new Promise(async (resolve, reject) => {

    const token = await getToken();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "firebase-uid": token
        },
      });

      let json = await response.json();
      // console.log('retrieved user', json)

      if (response.status >= 400 && response.status < 600) {
        throw "Error";
      }

      resolve(json);
    } catch (e) {
      console.log('error getting user...', e)
      reject(new Error(e));
    }
  });
};

// get user info from db
export const getDbInfo = async (uid) => {
  const url = "/api/v1/mobileusers/portalusers/" + uid;

  return new Promise(async (resolve, reject) => {
    const token = await getToken();
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "firebase-uid": token,
        },
      });

      let json = await response.json();
      if (response.status >= 400 && response.status < 600) {
        throw new Error("Issue retrieving user details");
      }

      resolve(json);

    } catch (e) {
      reject(new Error("Issue getting user info"));
    }
  });
};

export const getUserDbInfo = async (uid) => {
  const url = "/api/v1/mobileusers/user/" + uid;

  return new Promise(async (resolve, reject) => {
    try {
      const token = await getToken();
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "firebase-uid": token,
        },
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        throw new Error("Issue retrieving user details");
      }

      resolve(json);
    } catch (e) {
      reject(new Error("Issue getting user info"));
    }
  });
};

// TODO what is this for how is it secured
export const getUsers = async (uid) => {
  const url = "/api/v1/mobileusers/portalusers/" + uid;

  return new Promise(async (resolve, reject) => {
    try {
      const token = await getToken();
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "firebase-uid": token,
        },
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        throw new Error("Issue retrieving user details");
      }

      resolve(json);
    } catch (e) {
      reject(new Error("Issue getting user info"));
    }
  });
};

// TODO what is this for how do we secure it?
export const getAllUsers = async () => {
  const url = "/api/v1/mobileusers/portalusers?limit=100&skip=0";

  return new Promise(async (resolve, reject) => {
    const token = await getToken();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "Firebase-UID": token,
        },
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        throw new Error("Issue retrieving user details");
      }

      resolve(json);
    } catch (e) {
      reject(new Error("Issue getting user info"));
    }
  });
};
// TODO what is this for how do we secure it?
export const verifyUser = async (user) => {
  const url = "/api/v1/mobileusers/portalusers/" + user.firebaseId;

  return new Promise(async (resolve, reject) => {
    const token = await getToken();
    try {
      // make request
      const response = await fetch(url, {
        method: "PUT",
        body: JSON.stringify(user),
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "Firebase-UID": token,
        },
      });
      // handle response
      let json = await response.json();
      if (response.status >= 400 && response.status < 600) {
        throw new Error("Bad details");
      } else {
        resolve(json);
      }
    } catch (e) {
      reject(new Error("error getting user info"));
    }
  });
};

export const blockAccount = async (id) => {

  const url = "/api/v1/mobileusers/portalusers/" + id + "/block";

  return new Promise(async (resolve, reject) => {
    const token = await getToken();
    try {
      // make request
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "Firebase-UID": token,
        },
      });
      // handle response
      let json = await response.json();
      if (response.status >= 400 && response.status < 600) {
        throw new Error("Bad details");
      } else {
        resolve(json);
      }
    } catch (e) {
      reject(new Error("error blocking user info"));
    }
  });
};

// post feedback to email
export const postFeedback = async (feedback) => {
  return new Promise(async (resolve, reject) => {
    try {
      const token = await getToken();
      const response = await fetch("/api/v1/support/feedback?portal=true", {
        method: "POST",
        body: JSON.stringify(feedback),
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "Firebase-UID": token,
        },
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        throw new Error(json);
      }
      resolve(json);
    } catch (e) {
      reject(e);
    }
  });
};

// get all portals
export const getPortalAccounts = async (params) => {
  const token = await getToken();
  const url = `/api/v1/mobileusers/table/?iata=${params.iata}&firebase_info=true&approved=${params.approved}&limit=${params.limit || 25} &skip=${params.skip || 0}`

  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Firebase-UID": token,
        },
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        throw "Error";
      }
      resolve(json);
    } catch (e) {
      reject(new Error(e));
    }
  });
};
;

// send email
export const sendEmail = async (info) => {

  //'https://prmassist.com/api/v1/support/email'

  const url = "/api/v1/support/email";

  var data = {
    email: info.email,
    subject: info.subject,
    body: info.body,
    booking: info.booking
  };

  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify(data)
      });

      let json = await response.json();

      if (response.status >= 400 && response.status < 600) {
        throw "Error";
      }

      resolve(json);
    } catch (e) {
      reject(new Error(e));
    }
  });
};

export const getAirportByIata = async (params) => {

  const url = `/api/v1/bookings/airports/${params.code}?airline=${params.airline}`;

  return new Promise(async (resolve, reject) => {
    try {
      // the anonymous review page has no user: send no token rather than the public
      // key, which the gateway rejects on /api/v1/bookings paths
      const token = params.isReview ? await getToken().catch(() => null) : await getToken();
      const response = await fetch(url, { method: 'GET', headers: token ? { "Firebase-UID": token } : {} });
      let json = await response.json();
      if (response.status >= 400 && response.status < 600) {
        throw new Error("error getting airport")
      }
      resolve(json);
    } catch (e) {
      reject(new Error("error getting airport"));
    }
  });
};

export const resetPasswordService = async (email) => {
  const url = "/api/v1/mobileusers/portalusers/reset-password?email=" + email;

  return new Promise(async (resolve, reject) => {
    const token = await getToken();
    try {
      // make request
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Firebase-UID": token,
        },
      });
      // handle response
      let json = await response.json();
      if (response.status >= 400 && response.status < 600) {
        throw new Error("Bad details");
      } else {
        resolve(json);
      }
    } catch (e) {
      reject(new Error("error blocking user info"));
    }
  });
}
